import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useEffectOnce } from '@bwoty-web/ui-kit/hooks';
import { useCallback, useMemo } from 'react';

import { FilterBar } from '../components/FilterBar';
import { updateSubscriberLink } from '../utils/updateSubscriberLink';
import { updateQuery, filterRequestAction } from '../redux/actions';
import {
  getGTMClearFilterEvent,
  getGTMFilterTripTypeEvent,
  getGTMFilterPriceEvent,
  getGTMFilterDestinationEvent,
  getGTMShowFilteredEvent,
} from '../utils/gtmEvents';

import * as Selectors from '../redux/selectors';

const getDestinationCodesAndUpdateSubscriberLink = (currentDestinations, allDestinations) => {
  const destinationCodesBySelectedCountries = currentDestinations.flatMap(
    (selectedCountryCode) => {
      const country = allDestinations.find((c) => c.countryCode === selectedCountryCode);
      return country ? country.destinations.map((d) => d.code) : selectedCountryCode;
    },
  );
  updateSubscriberLink({ destinationCode: destinationCodesBySelectedCountries });
};

const filterDestinations = (data, selected, destinations, destToCountryMap, dispatch) => {
  const { code, value } = data;

  let current = selected.slice(0);
  let isCountry = false;

  if (value) {
    current.push(code);
  } else {
    current.splice(current.indexOf(code), 1);
  }

  if (destToCountryMap[code]) {
    // this is a destination, uncheck the country
    const countryCode = destToCountryMap[code];
    const cIndex = current.indexOf(countryCode);
    if (cIndex >= 0) {
      current.splice(cIndex, 1);
    }
  } else {
    // Remove all destination within a country if country is selected
    const country = destinations.find((c) => c.countryCode === code);
    if (country) {
      isCountry = true;
      const shouldRemove = country.destinations.map((d) => d.code);
      current = current.filter((val) => {
        return shouldRemove.indexOf(val) < 0;
      });
    }
  }

  dispatch(updateQuery({ destinationCode: current }));
  getDestinationCodesAndUpdateSubscriberLink(current, destinations);

  const gtm = value ? getGTMFilterDestinationEvent(code, isCountry) : null;
  dispatch(filterRequestAction({ gtm }));
};

const filterTripType = (data, tripTypes, selected, dispatch) => {
  const { code, value } = data;
  const current = selected.slice(0);

  if (value) {
    current.push(code);
  } else {
    current.splice(current.indexOf(code), 1);
  }

  dispatch(updateQuery({ tripTypes: current }));

  const gtm = value ? getGTMFilterTripTypeEvent(code, tripTypes) : null;
  dispatch(filterRequestAction({ gtm }));
};

const filterPrice = (price, dispatch) => {
  dispatch(updateQuery({ priceTo: price || '' }));
  dispatch(filterRequestAction({ gtm: getGTMFilterPriceEvent(price) }));
};

const closeFilterBar = (dispatch, gtmEvent) => {
  dispatch(updateQuery({ filter: false }, gtmEvent));
};

const FilterBarContainer = (props) => {
  const {
    destinations,
    destinationToCountry,
    dispatch,
    isFilterOpen,
    onResetFilters,
    priceRange,
    selectedDestinations,
    selectedPrice,
    selectedTripTypes,
    texts,
    totalCount,
    tripTypes,
  } = props;

  useEffectOnce(() => {
    if (selectedDestinations?.length > 0) {
      getDestinationCodesAndUpdateSubscriberLink(selectedDestinations, destinations);
    } else if (selectedPrice || selectedTripTypes?.length > 0) updateSubscriberLink();
  });

  const handleCloseFilterBar = () => {
    closeFilterBar(dispatch);
  };

  const handleFilterDestinations = useCallback(
    (data) => {
      filterDestinations(data, selectedDestinations, destinations, destinationToCountry, dispatch);
    },
    [dispatch, destinations, selectedDestinations, destinationToCountry],
  );

  const handleFilterTripType = useCallback(
    (data) => {
      filterTripType(data, tripTypes, selectedTripTypes, dispatch);
    },
    [dispatch, tripTypes, selectedTripTypes],
  );

  const handleFilterPrice = useCallback(
    (data) => {
      filterPrice(data.price, dispatch);
    },
    [dispatch],
  );

  const handleFilter = useCallback(
    (type, payload) => {
      if (type === 'destinations') {
        handleFilterDestinations(payload);
      } else if (type === 'price') {
        handleFilterPrice(payload);
      } else if (type === 'triptype') {
        handleFilterTripType(payload);
      }

      if (type === 'price' || type === 'triptype') updateSubscriberLink();
    },
    [handleFilterDestinations, handleFilterPrice],
  );

  const showFilteredLmsLabel = useMemo(() => {
    return (
      totalCount === 1 ? texts.showFilteredLmsLabelSingular : texts.showFilteredLmsLabelPlural
    ).replace('{lms}', totalCount);
  }, [totalCount, texts]);

  const handleReset = useCallback(
    (e) => {
      onResetFilters(e, getGTMClearFilterEvent());
    },
    [onResetFilters],
  );

  const handleShowFiltered = () => {
    closeFilterBar(dispatch, getGTMShowFilteredEvent());
  };

  return (
    <FilterBar
      isOpen={isFilterOpen}
      header={texts.filterHeader}
      onClose={handleCloseFilterBar}
      onFilter={handleFilter}
      tripTypeHeader={texts.filtertripTypeHeader}
      tripTypes={tripTypes}
      selectedTripTypes={selectedTripTypes}
      priceHeader={texts.filterPriceHeader}
      priceLabel={texts.priceSliderLabel}
      priceRange={priceRange}
      selectedPrice={selectedPrice}
      destinations={destinations}
      selectedDestinations={selectedDestinations}
      destinationsHeader={texts.filterDestinationHeader}
      resetFilterLabel={texts.resetFilterLabel}
      showFilteredLabel={showFilteredLmsLabel}
      onReset={handleReset}
      onShowFiltered={handleShowFiltered}
      totalCount={totalCount}
    />
  );
};

FilterBarContainer.propTypes = {
  dispatch: PropTypes.func,
  texts: PropTypes.object,
  priceRange: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
  isFilterOpen: PropTypes.bool,
  destinations: PropTypes.array,
  selectedDestinations: PropTypes.array,
  destinationToCountry: PropTypes.object,
  selectedPrice: PropTypes.any,
  tripTypes: PropTypes.any,
  selectedTripTypes: PropTypes.any,
  totalCount: PropTypes.number,
  onResetFilters: PropTypes.func,
};

function mapStateToProps(state) {
  const query = Selectors.getQueryParams(state);
  const selectedDestinations = Selectors.getSelectedDestinations(state);
  const selectedPrice = Selectors.getSelectedPrice(state);
  const selectedTripTypes = Selectors.getSelectedTripTypes(state);
  return {
    isFilterOpen: query.filter,
    destinations: Selectors.getDestinations(state),
    selectedDestinations,
    destinationToCountry: Selectors.getDestinationToCountryMap(state),
    priceRange: Selectors.getPriceRange(state),
    selectedPrice,
    tripTypes: Selectors.getTripTypes(state),
    selectedTripTypes,
    totalCount: Selectors.getTotalFilteredCount(state),
  };
}

export const ConnectedFilterBarContainer = connect(mapStateToProps)(FilterBarContainer);

export default ConnectedFilterBarContainer;
